import React, { useRef } from 'react'
import { Link } from 'react-router-dom'
import { motion, useInView } from 'framer-motion'
import { useOptimizedScroll } from '../hooks/useOptimizedScroll'
import { useIsMobile } from '../hooks/useMediaQuery'
import { PageMobileFooter } from './layout/PageMobileFooter'
import { termsOfServicePageFooter } from './layout/footerConfigs'

export const TermsOfServicePage: React.FC = () => {
  const isMobile = useIsMobile()
  const prefersReducedMotion = useRef(
    typeof window !== 'undefined'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false
  )

  React.useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Use optimized scroll hook to prevent flickering
  const { isScrolled: _isScrolled, scrollProgress } = useOptimizedScroll()

  // Get current date for "Last updated"
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })

  return (
    <>
      {/* Navbar is now global - no individual page navbars needed */}

      {/* Main Content Wrapper */}
      <div
        className="min-h-screen relative"
        style={{
          backgroundColor: '#F7F5F5',
          borderBottomLeftRadius: scrollProgress > 0 ? `${Math.min(scrollProgress * 48, 48)}px` : '48px',
          borderBottomRightRadius: scrollProgress > 0 ? `${Math.min(scrollProgress * 48, 48)}px` : '48px',
          boxShadow: scrollProgress > 0
            ? `0 ${20 + scrollProgress * 20}px ${60 + scrollProgress * 40}px rgba(0, 0, 0, ${0.2 + scrollProgress * 0.15})`
            : '0 20px 60px rgba(0, 0, 0, 0.2)',
          overflow: 'hidden',
          zIndex: 10,
          paddingBottom: '100px',
          transition: 'border-radius 0.1s ease-out, box-shadow 0.2s ease-out',
        }}
      >
        {/* Animated Background Gradient Orbs */}
        <motion.div className="absolute -top-40 -left-40 w-96 h-96 rounded-full opacity-20 blur-3xl" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', willChange: prefersReducedMotion.current ? 'auto' : 'transform' }} animate={prefersReducedMotion.current ? {} : { scale: [1, 1.2, 1], x: [0, 50, 0], y: [0, 30, 0] }} transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }} />
        <motion.div className="absolute top-1/2 -right-40 w-96 h-96 rounded-full opacity-20 blur-3xl" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', willChange: prefersReducedMotion.current ? 'auto' : 'transform' }} animate={prefersReducedMotion.current ? {} : { scale: [1, 1.3, 1], x: [0, -50, 0], y: [0, -30, 0] }} transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut' }} />
        <motion.div className="absolute bottom-20 left-1/3 w-80 h-80 rounded-full opacity-15 blur-3xl" style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', willChange: prefersReducedMotion.current ? 'auto' : 'transform' }} animate={prefersReducedMotion.current ? {} : { scale: [1, 1.15, 1], x: [0, 30, 0], y: [0, -20, 0] }} transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }} />

        <HeroSection prefersReducedMotion={prefersReducedMotion.current} currentDate={currentDate} />
        <ContentSection prefersReducedMotion={prefersReducedMotion.current} />
      </div>

      <FooterSection isMobile={isMobile} />
    </>
  )
}

const HeroSection: React.FC<{ prefersReducedMotion: boolean; currentDate: string }> = ({ prefersReducedMotion, currentDate }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.3 })

  return (
    <section ref={ref} className="relative pt-48 pb-32" style={{ backgroundColor: 'transparent' }}>
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.h1 className="text-5xl md:text-6xl lg:text-7xl mb-4 leading-tight" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600, letterSpacing: '-0.03em' }} initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}>
          Terms of Service
        </motion.h1>
        <motion.p className="text-base mb-8" style={{ color: '#999999', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400 }} initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 0.2, ease: [0.4, 0.0, 0.2, 1] }}>
          Last updated: {currentDate}
        </motion.p>
        <motion.p className="text-lg md:text-xl max-w-3xl mx-auto leading-relaxed" style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 500 }} initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 0.4, ease: [0.4, 0.0, 0.2, 1] }}>
          These Terms of Service ("Terms") govern your use of Mernaki's websites, services, software, and related platforms. By accessing any Mernaki product or service, you agree to these Terms.
        </motion.p>
      </div>
    </section>
  )
}


const ContentSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.1 })

  const sections = [
    {
      title: '1. What Mernaki Provides (Scope of Services)',
      subsections: [
        {
          subtitle: '1.1 Digital Services',
          content: ['Rare Earth services', 'VerpackG services', 'Compliance recall', 'ReCommerce tools', 'Hiring templates']
        },
        {
          subtitle: '1.2 Software Products',
          content: ['JobMeet'],
          isBlurred: false
        },
        {
          subtitle: '1.3 In-Development Projects',
          content: ['Project Velocity', 'Project Crimson', 'Project Nexus'],
          isBlurred: true
        },
        {
          subtitle: '1.4 Hardware & Prototypes',
          content: ['Project Quantum device concepts'],
          isBlurred: true
        },
        {
          subtitle: '1.5 Additional Subdomains',
          content: ['meet.mernaki.de', 'hr.mernaki.de', 'rare.mernaki.de', 'compliance.mernaki.de', 'And others']
        }
      ]
    },
    {
      title: '2. Eligibility / User Requirements',
      content: [
        'Must be 18 years or older',
        'Must use real business information',
        'Must not misuse systems or services'
      ]
    },
    {
      title: '3. Account Responsibilities',
      content: [
        'Keep credentials secure and confidential',
        'No unauthorized access to accounts',
        'Accurate information required for all registrations'
      ]
    },
    {
      title: '4. Fraud Prevention & Device Fingerprinting',
      intro: 'To prevent trial abuse and ensure fair access to our services, we collect device information:',
      subsections: [
        {
          subtitle: '4.1 Device Fingerprinting',
          content: [
            'We generate a unique device identifier (fingerprint) during account registration',
            'Purpose: Prevent multiple trial accounts from the same device',
            'Data collected: Browser configuration, screen resolution, installed fonts, timezone',
            'No personal information: The fingerprint is anonymous and cannot identify you personally'
          ],
          isBlurred: false
        },
        {
          subtitle: '4.2 IP Address Tracking',
          content: [
            'We track IP addresses to limit trial account creation',
            'Maximum 2 trial accounts per IP address within 90 days',
            'Corporate networks can be whitelisted by contacting support@mernaki.de'
          ],
          isBlurred: false
        },
        {
          subtitle: '4.3 Email Tracking',
          content: [
            'Each email address can only be used for one trial account',
            'Deleting your account does not reset this limitation',
            'This prevents abuse of the trial system'
          ],
          isBlurred: false
        },
        {
          subtitle: '4.4 Data Retention',
          content: [
            'Fraud prevention data is retained permanently',
            'This is necessary to maintain the integrity of our trial system',
            'Data is stored securely and used only for fraud prevention'
          ],
          isBlurred: false
        },
        {
          subtitle: '4.5 Your Rights (GDPR)',
          content: [
            'You have the right to access your data',
            'You can request information about collected fingerprints',
            'For appeals or questions: support@mernaki.de',
            'False positives can be reviewed on a case-by-case basis'
          ],
          isBlurred: false
        }
      ]
    },
    {
      title: '5. Acceptable Use Policy (AUP)',
      intro: 'Users are prohibited from:',
      content: [
        'Scraping servers or automated data collection',
        'Reverse-engineering service code or software',
        'Bypassing security measures or authentication',
        'Impersonating others or creating fake accounts',
        'Using services for illegal activity',
        'Abusing Rare Earth permit system',
        'Spam or automated use without permission'
      ]
    },
    {
      title: '6. Payments & Billing',
      content: [
        'Subscription terms apply where applicable',
        'No refunds unless required by law',
        'Auto-billing rules may apply',
        'VAT and EU regulations compliance'
      ]
    },
    {
      title: '7. Service Availability & Uptime',
      content: [
        'No guaranteed uptime or availability',
        'Maintenance may occur with or without notice',
        'Status updates available at status.mernaki.de',
        'Services may change or be discontinued'
      ]
    },
    {
      title: '8. Intellectual Property',
      content: [
        'Logo, branding, and UI are Mernaki\'s property',
        'Users cannot copy designs or branding',
        'Software code belongs to Mernaki',
        'Hardware device designs cannot be replicated'
      ]
    },
    {
      title: '9. AI Tools Disclaimer',
      content: [
        'AI outputs may be incorrect or incomplete',
        'Users must verify all information independently',
        'No liability for decisions based on AI responses',
        'AI tools are provided "as is" without warranties'
      ]
    },
    {
      title: '10. Changes to Services & Terms',
      content: [
        'Mernaki may update these Terms at any time',
        'Users will be notified of major changes',
        'Continued use constitutes acceptance of new Terms'
      ]
    },
    {
      title: '11. Limitation of Liability',
      intro: 'Mernaki is not responsible for:',
      content: [
        'Business losses or lost profits',
        'Server downtime or service interruptions',
        'Misuse by users or third parties',
        'Third-party integrations (Vercel, Railway, Cloudflare, email providers)',
        'Data loss or corruption',
        'Indirect, incidental, or consequential damages'
      ]
    },
    {
      title: '12. Governing Law',
      content: [
        'These Terms are governed by the laws of Germany',
        'Venue and jurisdiction are in Munich, Germany',
        'Disputes shall be resolved under German law'
      ]
    },
    {
      title: '13. Contact Information',
      content: [
        'For legal questions: legal@mernaki.de',
        'For support: support@mernaki.de'
      ]
    }
  ]

  return (
    <section ref={ref} className="relative py-16" style={{ backgroundColor: 'transparent' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {sections.map((section, index) => (
          <motion.div
            key={index}
            className="mb-12"
            initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: index * 0.05, ease: [0.4, 0.0, 0.2, 1] }}
          >
            <h2 className="text-2xl md:text-3xl mb-4" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>
              {section.title}
            </h2>

            {section.intro && (
              <p className="mb-3" style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontSize: '16px', lineHeight: '1.8' }}>
                {section.intro}
              </p>
            )}

            {section.subsections ? (
              <div className="space-y-6">
                {section.subsections.map((subsection, subIndex) => (
                  <div key={subIndex}>
                    <h3 className="text-lg mb-3" style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}>
                      {subsection.subtitle}
                    </h3>
                    <ul className="space-y-2 ml-6">
                      {subsection.content.map((item, itemIndex) => (
                        <li
                          key={itemIndex}
                          style={{
                            color: '#666666',
                            fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                            fontSize: '16px',
                            lineHeight: '1.8',
                            listStyleType: 'disc',
                            filter: subsection?.isBlurred ? 'blur(3px)' : 'none',
                            userSelect: subsection?.isBlurred ? 'none' : 'auto',
                            pointerEvents: subsection?.isBlurred ? 'none' : 'auto'
                          }}
                        >
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ) : (
              <ul className="space-y-2 ml-6">
                {section.content?.map((item, itemIndex) => (
                  <li
                    key={itemIndex}
                    style={{
                      color: '#666666',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontSize: '16px',
                      lineHeight: '1.8',
                      listStyleType: 'disc'
                    }}
                  >
                    {item}
                  </li>
                ))}
              </ul>
            )}
          </motion.div>
        ))}
      </div>
    </section>
  )
}


const FooterSection: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
  if (isMobile) {
    return <PageMobileFooter {...termsOfServicePageFooter} />
  }

  return (
    <footer className="relative" style={{ backgroundColor: '#0E0E0E', position: 'sticky', bottom: 0, zIndex: 1, paddingTop: '140px', paddingBottom: '40px', marginTop: '-80px' }}>
      <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-8 mb-12">

          {/* Left: Brand */}
          <div className="flex items-center gap-3">
            <img
              src="/Mernaki_Logo_Pack/transparent/mernaki-128.png"
              alt="Mernaki Logo"
              className="object-contain"
              style={{ height: '42px', width: 'auto', opacity: 0.85, filter: 'brightness(0) invert(1)' }}
              loading="lazy"
            />
            <h3
              className="text-lg"
              style={{
                color: '#FFFFFF',
                fontFamily: "'Brush Script MT', 'Lucida Handwriting', 'Apple Chancery', cursive",
                fontWeight: 400
              }}
            >
              Mernaki
            </h3>
          </div>

          {/* Center: Links */}
          <div className="flex items-center gap-6 flex-wrap justify-center">
            <Link
              to="/privacy-policy"
              className="text-sm transition-all duration-300"
              style={{
                color: 'rgba(255, 255, 255, 0.5)',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 400,
                textDecoration: 'none'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
              onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
            >
              Privacy Policy
            </Link>
            <span style={{ color: 'rgba(255, 255, 255, 0.2)' }}>|</span>
            <span
              className="text-sm"
              style={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 500
              }}
            >
              Terms of Service
            </span>
            <span style={{ color: 'rgba(255, 255, 255, 0.2)' }}>|</span>
            <Link
              to="/impressum"
              className="text-sm transition-all duration-300"
              style={{
                color: 'rgba(255, 255, 255, 0.5)',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 400,
                textDecoration: 'none'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
              onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
            >
              Impressum
            </Link>
          </div>

          {/* Right: Contact */}
          <div className="text-center md:text-right">
            <a
              href="mailto:support@mernaki.de"
              className="text-sm transition-all duration-300"
              style={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 500,
                textDecoration: 'none'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 1)'}
              onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)'}
            >
              support@mernaki.de
            </a>
          </div>
        </div>

        {/* Bottom Row */}
        <div
          className="pt-8 text-center"
          style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}
        >
          <p
            className="text-sm"
            style={{
              color: 'rgba(255, 255, 255, 0.3)',
              fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
              fontWeight: 300
            }}
          >
            © {new Date().getFullYear()} Mernaki. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  )
}
